import {
  pageContextDescriptorSchema,
  type PageContextDescriptor,
} from "@repo/app-types/agents/pageContext";
import { useCallback, useMemo, useState } from "react";
import {
  NavigationType,
  useLocation,
  useMatches,
  useNavigate,
  useNavigationType,
  type To,
  type UIMatch,
} from "react-router";
import z from "zod";
import { resolveCopilotSurface } from "./copilotRouteRegistry.js";

/**
 * Builds one fragment of the raw page-context descriptor for a matched route.
 * Fragments are merged from the shallowest match to the deepest, so a parent
 * playbook route can contribute `playbookId` while a nested teammate route
 * overrides `page` and contributes `teammateId`. A slideover can likewise
 * override `page` for the resource in front without restating ancestor scope.
 * The merged descriptor is validated once after every fragment is applied.
 */
export type BuildPageContext = (match: UIMatch) => Record<string, unknown>;

const pageContextHandle = z.object({
  pageContext: z.custom<BuildPageContext>(
    (value) => typeof value === "function",
  ),
});

function checkIsMatchWithPageContext<T extends UIMatch>(
  match: T,
): match is T & { handle: z.infer<typeof pageContextHandle> } {
  return pageContextHandle.safeParse(match.handle).success;
}

/**
 * Derives the page-context descriptor from all matched route fragments, or
 * undefined when none carries page context. Ids are validated (and branded) by
 * the schema, so a malformed param yields undefined rather than a broken
 * descriptor.
 */
export function buildPageContextDescriptor(
  matches: readonly UIMatch[],
): PageContextDescriptor | undefined {
  const matchesWithPageContext = matches.filter(checkIsMatchWithPageContext);
  if (matchesWithPageContext.length === 0) {
    return undefined;
  }
  const rawDescriptor = Object.assign(
    {},
    ...matchesWithPageContext.map((match) => match.handle.pageContext(match)),
  );
  const parsed = pageContextDescriptorSchema.safeParse(rawDescriptor);
  return parsed.success ? parsed.data : undefined;
}

/** The descriptor for the current route, recomputed as the user navigates. */
export function useCopilotPageContext(): PageContextDescriptor | undefined {
  const matches = useMatches();
  return useMemo(() => buildPageContextDescriptor(matches), [matches]);
}

/**
 * Pathname of the configuration page the user is on, or null when they are not
 * on one. Each matched route is checked with the descriptor it and its
 * ancestors build, from the root down; the first whose page opens the panel is
 * the configuration page. The routes below it are its tabs or a slideover
 * opened over it, so they share its pathname rather than counting as a new
 * page.
 */
export function findConfigurationPagePath(
  matches: readonly UIMatch[],
): string | null {
  for (const [index, match] of matches.entries()) {
    const descriptor = buildPageContextDescriptor(matches.slice(0, index + 1));
    if (descriptor && resolveCopilotSurface(descriptor).opensPanel) {
      return match.pathname;
    }
  }
  return null;
}

const keepsCopilotChatStateSchema = z.object({
  keepsCopilotChat: z.literal(true),
});

/**
 * `navigate` that keeps the current copilot chat on the configuration page it
 * lands on, even when the chat is idle. Returning to the entry through history
 * does not.
 */
export function useNavigateKeepCopilotChat() {
  const navigate = useNavigate();
  return useCallback(
    (to: To, options?: { replace?: boolean }) =>
      navigate(to, { ...options, state: { keepsCopilotChat: true } }),
    [navigate],
  );
}

function checkNavigationKeepsCopilotChat(locationState: unknown): boolean {
  return keepsCopilotChatStateSchema.safeParse(locationState).success;
}

/**
 * Calls `onArrive` when the user arrives on a configuration page, with whether
 * the navigation keeps the copilot chat. Runs during render, ahead of the
 * page's own mount effects.
 */
export function useConfigurationPageArrival(
  onArrive: (keepsCopilotChat: boolean) => void,
) {
  const configurationPagePath = findConfigurationPagePath(useMatches());
  const location = useLocation();
  const navigationType = useNavigationType();
  const [previousConfigurationPagePath, setPreviousConfigurationPagePath] =
    useState<string | null>(null);

  // Same page, or another of its tabs
  if (configurationPagePath === previousConfigurationPagePath) {
    return;
  }
  setPreviousConfigurationPagePath(configurationPagePath);

  // Left for a page that isn't a configuration page
  if (configurationPagePath === null) {
    return;
  }
  onArrive(
    navigationType !== NavigationType.Pop &&
      checkNavigationKeepsCopilotChat(location.state),
  );
}
